import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getMyOrders, getOrder, formatIDR, STATUS_LABELS, PLAN_LABELS } from '../services/portalService';
import { devError } from '../utils/devLog';
import OrderDetail from './OrderDetail';
import './portal.css';

const Dashboard = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const orderId = params.get('order');
  const initialTab = params.get('tab') || 'overview';

  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadList = useCallback(async () => {
    try {
      const list = await getMyOrders();
      setOrders(list);
      return list;
    } catch (err) {
      devError('Failed to load orders:', err);
      setOrders([]);
      return [];
    }
  }, []);

  const loadSelected = useCallback(async (id) => {
    try { setSelected(await getOrder(id)); }
    catch { setSelected(null); }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      await loadList();
      if (!cancelled) {
        if (orderId) await loadSelected(orderId);
        else setSelected(null);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [orderId, loadList, loadSelected]);

  const openOrder = (id) => setParams({ order: id });
  const back = () => { setParams({}); setSelected(null); };
  const refresh = async () => { await loadList(); if (orderId) await loadSelected(orderId); };

  const displayName = user?.user_metadata?.full_name || user?.user_metadata?.name || user?.email;
  const avatar = user?.user_metadata?.avatar_url || user?.user_metadata?.picture;

  return (
    <div className="portal">
      <div className="portal-wrap">
        <div className="portal-topbar">
          <div>
            <p className="portal-eyebrow">Client Portal</p>
            <h1 className="portal-title">Your projects</h1>
          </div>
          <div className="portal-user">
            {avatar && <img className="portal-avatar" src={avatar} alt="" referrerPolicy="no-referrer" />}
            <div>
              <div className="portal-user-name">{displayName}</div>
              <button onClick={signOut} className="portal-user-mail" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Sign out</button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="portal-spinner" />
        ) : selected ? (
          <OrderDetail order={selected} onBack={back} onChanged={refresh} initialTab={initialTab} />
        ) : orders.length === 0 ? (
          <div className="portal-card portal-empty">
            <div className="portal-empty-emoji">🚀</div>
            <h3 style={{ fontFamily: 'var(--v2-font-display)', fontSize: 24, margin: '10px 0' }}>No projects yet</h3>
            <p className="portal-muted" style={{ maxWidth: 420, margin: '0 auto 20px' }}>
              Start a project from the pricing page — choose a package and how you’d like to pay.
            </p>
            <button className="portal-btn portal-btn-primary" onClick={() => navigate('/pricing')}>Browse packages</button>
          </div>
        ) : (
          <div>
            {orders.map((o) => {
              const total = Number(o.total_amount) || 0;
              const paid = Number(o.amount_paid) || 0;
              return (
                <button key={o.id} className="order-item" onClick={() => openOrder(o.id)}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                    <div>
                      <div className="order-item-name">{o.package_name}</div>
                      <div className="order-item-meta">{o.order_number} · {PLAN_LABELS[o.payment_plan]} · {formatIDR(total)}</div>
                    </div>
                    <span className={`pill pill-${o.status}`}>{STATUS_LABELS[o.status] || o.status}</span>
                  </div>
                  <div className="portal-progress" style={{ marginTop: 12 }}>
                    <span style={{ width: `${total ? Math.min(100, (paid / total) * 100) : 0}%` }} />
                  </div>
                </button>
              );
            })}
            <button className="portal-btn portal-btn-ghost" style={{ marginTop: 8 }} onClick={() => navigate('/pricing')}>+ Start another project</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
